/* eslint-disable no-redeclare */
import { NUMBER_CONSTRAINT, STRING_CONSTRAINT } from "@flockcover/flock-svc-core-constraints"
import { cast, LocalDateTime, maybe, Money } from "@flockcover/flock-svc-core-types"
import { nullable } from "@flockcover/platform-types-utility"
import { EmbeddedMotorVehicleType } from "@shared/types"
import * as t from "io-ts"
import { UUID } from "io-ts-types/lib/UUID"

const SectorType = t.union([
  t.literal(`Courier`),
  t.literal(`Food Distribution`),
  t.literal(`Self Drive Hire`),
  t.literal(`Subscription`),
  t.literal(`Taxi`),
  t.literal(`Trades`),
])

const ProductType = t.union([t.literal(`FLAT_FEE`), t.literal(`UBI`)])

const DistributorBrokerageType = t.type({
  distributor: t.string,
  brokerage: Money,
})

const PricingType = t.type({
  premium: Money,
  tax: Money,
  fees: Money,
  brokerages: t.readonlyArray(DistributorBrokerageType),
  totalPremium: Money,
})

const Premium = t.type({
  net: t.number,
  brokerage: t.number,
  tax: t.number,
  fee: t.number,
  grossDiscount: t.number,
  taxDiscount: t.number,
})

const Deposit = t.type({ rate: t.number, premium: Premium })

const BillingProcess = t.union([
  t.type({ type: t.literal(`bundled`) }),
  t.type({ type: t.literal(`deposit_only`), deposit: Deposit }),
  t.type({ type: t.literal(`distributed`), deposit: Deposit }),
  t.type({ type: t.literal(`premium_finance`), deposit: Deposit, provider: t.string }),
])

const PolicyMeta = t.intersection([
  t.type({
    vertical: t.string,
    productType: t.string,
    billingProcess: BillingProcess,
  }),

  t.partial({
    modern: t.boolean,
    fleetId: UUID,
  }),
])

const ConnectivityType = t.type({
  onCoverTotal: t.number,
  connectedTotal: t.number,
  disconnectedTotal: t.number,
  connectivityRate: maybe(t.number),
})

export const PremiumBandType = t.type({
  label: t.string,
  minPerMileCost: maybe(Money),
  maxPerMileCost: maybe(Money),
  fixedGrossPremium: maybe(Money),
})

export const Schedule = t.type({
  id: UUID,
  documentUrl: nullable(t.string),
  // @TODO - there is more properties on a schedule but this is all i need for now
})

const PolicyType = t.intersection([
  t.type({
    id: UUID,
    number: t.string,
    type: t.literal(`MotorFlatFeePolicy`),
    holder: t.string,
    productType: ProductType,
    sector: SectorType,
    startTime: LocalDateTime,
    endTime: LocalDateTime,
    excess: Money,
    currency: t.string,
    connectivity: maybe(ConnectivityType),
    underwriter: t.type({
      name: t.string,
      link: t.string,
    }),
    premiumBands: t.readonlyArray(PremiumBandType),
    pricing: PricingType,
    fleet: t.readonlyArray(EmbeddedMotorVehicleType),
    numberOfInstallments: t.number,
    schedules: t.any, // TODO type it
    effectiveSchedule: Schedule,
  }),
  t.partial({
    meta: PolicyMeta,
  }),
])

const PolicySummaryEntryType = t.type({
  description: t.string,
  amount: Money,
})

export const ScheduleBandType = t.type({
  title: t.string,
  rate: t.number,
  abiGroup: nullable(NUMBER_CONSTRAINT),
  vehicleType: nullable(STRING_CONSTRAINT),
  grossWeight: nullable(NUMBER_CONSTRAINT),
})

// --------------------------------------------------------------------------------------------------------------------

export type Connectivity = t.TypeOf<typeof ConnectivityType>
export type DistributorBrokerage = t.TypeOf<typeof DistributorBrokerageType>
export type Policy = t.TypeOf<typeof PolicyType>
export type PolicySummaryEntry = t.TypeOf<typeof PolicySummaryEntryType>
export type Pricing = t.TypeOf<typeof PricingType>
export type PremiumBand = t.TypeOf<typeof PremiumBandType>
export type Sector = t.TypeOf<typeof SectorType>
export type ScheduleBand = t.TypeOf<typeof ScheduleBandType>

export const makePolicy = cast(PolicyType)
